import { memo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  ATTEMPT_DERIVED_STATUS,
  ATTEMPT_DERIVED_STATUS_COLORS_MAP,
  ATTEMPT_DERIVED_STATUS_LABEL,
  DATE_TIME_FORMATS,
  TIME_FORMAT,
} from "@miplatform/constants";
import {
  getDurationInHours,
  getFormattedDuration,
  toFormattedDate,
} from "@miplatform/utils";
import { Info } from "@mibuildweb/components";
import { Box, Typography } from "@mui/material";
import { CLOSEOUT_SCREENS } from "../constants";

const getStatus = (derivedStatus, issues, unfilteredIssues, activeScreen) => {
  const status = ATTEMPT_DERIVED_STATUS_LABEL[derivedStatus];

  if (activeScreen === CLOSEOUT_SCREENS.QC_CAMERA) {
    return status;
  }

  if (derivedStatus === ATTEMPT_DERIVED_STATUS.PENDING_CLOSEOUT) {
    const resolvedIssues = issues.reduce((acc, issue) => {
      if (issue.issueResult) {
        return acc + 1;
      }

      return acc;
    }, 0);

    return `${status} ${resolvedIssues}/${issues.length}`;
  }

  if (derivedStatus === ATTEMPT_DERIVED_STATUS.PENDING_PREBILL) {
    const resolvedIssues = unfilteredIssues.reduce((acc, issue) => {
      if (issue.invoiceItem?.subItems?.length > 0) {
        return acc + 1;
      }

      return acc;
    }, 0);

    return `${status} ${resolvedIssues}/${unfilteredIssues.length}`;
  }

  return status;
};

export const CloseoutDrawerSubHeader = memo(
  ({
    derivedStatus,
    scheduledDate,
    driveStartTime,
    driveEndTime,
    createdOn,
    duration,
    diffInDuration,
    jobNumber,
    isExtended,
    issues = [],
    unfilteredIssues = [],
    activeScreen,
  }) => {
    const { classes } = useStyles({
      isExtended,
      colors: ATTEMPT_DERIVED_STATUS_COLORS_MAP[derivedStatus] || {},
    });
    const resolvedDriveEndTime = driveEndTime || new Date();
    return (
      <Box className={classes.root}>
        <Box className={classes.statusContainer}>
          <Info
            text="Order Status"
            subtext={getStatus(
              derivedStatus,
              issues,
              unfilteredIssues,
              activeScreen
            )}
            maxWidth={150}
            descriptionColor={
              ATTEMPT_DERIVED_STATUS_COLORS_MAP[derivedStatus]?.title
            }
          />
        </Box>
        <Box className={classes.infoContainer}>
          <Info
            text="Performed On"
            subtext={
              scheduledDate
                ? toFormattedDate(
                    scheduledDate,
                    DATE_TIME_FORMATS.FORMAT_MM_DD_YYYY
                  )
                : "--"
            }
          />
          {driveStartTime && (
            <Info
              text="Drive Time"
              subtext={`${getFormattedDuration(
                getDurationInHours(driveStartTime, resolvedDriveEndTime)
              )} (${toFormattedDate(
                driveStartTime,
                TIME_FORMAT
              )} - ${toFormattedDate(resolvedDriveEndTime, TIME_FORMAT)})`}
              maxWidth={180}
            />
          )}
          <Info
            text="Total Time"
            subtext={
              <Box display="flex" alignItems="center" gap={1}>
                {[
                  ATTEMPT_DERIVED_STATUS.NEW,
                  ATTEMPT_DERIVED_STATUS.ON_HOLD,
                ].includes(derivedStatus)
                  ? "--:--"
                  : getFormattedDuration(duration)}
                {diffInDuration ? (
                  <Typography className={classes.diffTotalTimeText}>
                    ({diffInDuration > 0 ? "+" : "-"}
                    {getFormattedDuration(Math.abs(diffInDuration))})
                  </Typography>
                ) : null}
              </Box>
            }
          />
          <Info text="Job Number" subtext={jobNumber || "--"} />
          <Info
            text="Created On"
            subtext={
              createdOn
                ? toFormattedDate(
                    createdOn,
                    DATE_TIME_FORMATS.FORMAT_MM_DD_YYYY
                  )
                : "--"
            }
          />
        </Box>
      </Box>
    );
  }
);

CloseoutDrawerSubHeader.displayName = "CloseoutDrawerSubHeader";

const useStyles = makeStyles()((theme, { isExtended, colors }) => ({
  root: {
    display: "flex",
    gap: theme.spacing(4),
    alignItems: "center",
    borderRadius: theme.spacing(0.5),
    border: `1px solid ${colors.main}`,
    backgroundColor: colors.background,
  },
  statusContainer: {
    padding: theme.spacing(2, 7, 2, 4),
    backgroundColor: colors.statusBackground,
    borderRight: `1px solid ${colors.main}`,
  },
  infoContainer: {
    display: "flex",
    gap: theme.spacing(8),
    padding: theme.spacing(1, 3),
    alignItems: "center",
  },
  diffTotalTimeText: {
    fontWeight: theme.fontWeight.semiBold,
    color: isExtended ? theme.palette.red.main : theme.palette.green.main,
    fontSize: theme.typography.pxToRem(11),
  },
}));
